import { useState, type ReactNode } from "react";
import { AdminLayout } from "../../components/AdminLayout";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { adminHeaders, api } from "../../lib/api";
import { isLocalDevFrontend } from "../../lib/env";

type TestDataAction = {
  key: string;
  title: string;
  message: string;
  confirmText: string;
  success: string;
  variant?: "normal" | "danger";
  run: () => Promise<unknown>;
};

function TestDataToolCard({
  eyebrow,
  title,
  description,
  tone = "normal",
  children
}: {
  eyebrow: string;
  title: string;
  description: string;
  tone?: "normal" | "danger";
  children: ReactNode;
}) {
  return (
    <article className={`test-data-tool-card ${tone === "danger" ? "danger" : ""}`}>
      <div className="test-data-tool-card-head">
        <span>{eyebrow}</span>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      {children}
    </article>
  );
}

function ActionLine({
  title,
  description,
  children
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="test-data-action-line">
      <div>
        <strong>{title}</strong>
        <span>{description}</span>
      </div>
      <div className="test-data-action-controls">{children}</div>
    </div>
  );
}

function safeErrorMessage(err: unknown) {
  if (err instanceof Error && err.message && err.message !== "undefined" && err.message !== "null") {
    return err.message;
  }
  return "操作失敗，請稍後再試。";
}

export function AdminTestDataPage() {
  const [memberAccount, setMemberAccount] = useState("test003");
  const [inviteeAccount, setInviteeAccount] = useState("test004");
  const [amount, setAmount] = useState(1000);
  const [depositedAt, setDepositedAt] = useState(new Date().toISOString());
  const [note, setNote] = useState("local test deposit");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [pendingAction, setPendingAction] = useState<TestDataAction | null>(null);
  const [activeActionKey, setActiveActionKey] = useState<string | null>(null);

  const account = memberAccount.trim();
  const invitee = inviteeAccount.trim();
  const weekendTestNow = "2026-07-04T10:00:00+08:00";
  const isLocalDev = isLocalDevFrontend();
  const isBusy = Boolean(activeActionKey);

  function requestAction(action: TestDataAction) {
    if (isBusy) return;
    setMessage("");
    setError("");
    setPendingAction(action);
  }

  async function confirmPendingAction() {
    if (!pendingAction || activeActionKey) return;
    const action = pendingAction;
    setActiveActionKey(action.key);
    setMessage("");
    setError("");
    try {
      await action.run();
      setMessage(action.success);
      setPendingAction(null);
    } catch (err) {
      setError(safeErrorMessage(err));
    } finally {
      setActiveActionKey(null);
    }
  }

  function actionLabel(key: string, label: string) {
    return activeActionKey === key ? "處理中..." : label;
  }

  function runTest008Action(action: string, success: string) {
    return api("/api/admin/test-data/test008", {
      method: "POST",
      headers: adminHeaders(),
      body: JSON.stringify({ member_account: "test008", action })
    }).then(() => success);
  }

  function goAsTest008(path: string) {
    localStorage.setItem("current_member_account", "test008");
    localStorage.setItem("memberAccount", "test008");
    window.location.href = path;
  }

  if (!isLocalDev) {
    return (
      <AdminLayout title="測試資料">
        <section className="admin-card test-data-alert">
          <div className="admin-card-head"><span>LOCAL DEV ONLY</span><h2>此頁僅 local/dev 可用</h2></div>
          <p className="admin-muted">正式環境不提供測試資料建立、清除或測試流程操作。</p>
          <a className="admin-table-link" href="/admin">返回後台首頁</a>
        </section>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="測試資料">
      <section className="admin-card test-data-alert">
        <div className="admin-card-head"><span>LOCAL DEV</span><h2>本機測試資料工具</h2></div>
        <p className="admin-muted">
          此頁只供 local/dev 測試使用。正式環境不得顯示，也不得執行任何測試資料建立或清除操作。
        </p>
        <p className="status-pill soon">本機測試模式，測試資料入口只會在本機環境顯示。</p>
        {message ? <p className="result">{message}</p> : null}
        {error ? <p className="error-box">{error}</p> : null}
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>COMMON TEST DATA</span>
          <h2>常用測試資料</h2>
        </div>
        <p className="admin-muted">一般建立資料操作集中在這裡。每個會寫資料的操作都會先跳出確認，避免誤按。</p>

        <div className="test-data-tool-grid">
          <TestDataToolCard
            eyebrow="TEST DEPOSIT"
            title="新增測試存款"
            description="用來測會員存款、入盟啟動筆、常態回饋或新人關卡前置資料。"
          >
            <div className="test-data-form-grid">
              <label>會員帳號<input value={memberAccount} onChange={(event) => setMemberAccount(event.target.value)} /></label>
              <label>金額<input type="number" value={amount} onChange={(event) => setAmount(Number(event.target.value))} /></label>
              <label>存款時間<input value={depositedAt} onChange={(event) => setDepositedAt(event.target.value)} /></label>
              <label>備註<input value={note} onChange={(event) => setNote(event.target.value)} /></label>
            </div>
            <div className="test-data-action-row">
              <button
                className="admin-btn admin-btn-primary"
                type="button"
                disabled={isBusy || !account || amount <= 0}
                onClick={() => requestAction({
                  key: "create-test-deposit",
                  title: "確認建立測試存款？",
                  message: `系統會為 ${account || "此會員"} 建立一筆 local/dev 測試存款。請確認會員帳號、金額與時間無誤。`,
                  confirmText: "確認建立",
                  success: "測試存款已建立。",
                  run: () => api("/api/admin/test-deposit", {
                    method: "POST",
                    headers: adminHeaders(),
                    body: JSON.stringify({ member_account: account, amount, deposited_at: depositedAt, note })
                  })
                })}
              >
                {actionLabel("create-test-deposit", "建立測試存款")}
              </button>
            </div>
          </TestDataToolCard>

          <TestDataToolCard
            eyebrow="NEWBIE TEST"
            title="新人七關測試資料"
            description="用來測新人第 1～7 關流程。第 3 / 5 關仍需到會員詳情選定存款。"
          >
            <label className="test-data-inline-input">會員帳號<input value={memberAccount} onChange={(event) => setMemberAccount(event.target.value)} /></label>
            <div className="test-data-action-list">
              <ActionLine title="第 2 關解鎖" description="讓會員進入第 2 關可驗收狀態。">
                <button
                  className="admin-btn admin-btn-action"
                  type="button"
                  disabled={isBusy || !account}
                  onClick={() => requestAction({
                    key: "unlock-step2",
                    title: "確認開啟第 2 關？",
                    message: `系統會為 ${account || "此會員"} 開啟新人第 2 關測試狀態。`,
                    confirmText: "確認開啟",
                    success: "第 2 關已開啟。",
                    run: () => api(`/api/admin/members/${encodeURIComponent(account)}/unlock-newbie-step2-local`, {
                      method: "POST",
                      headers: adminHeaders(),
                      body: JSON.stringify({})
                    })
                  })}
                >
                  {actionLabel("unlock-step2", "測試第 2 關解鎖")}
                </button>
              </ActionLine>

              <ActionLine title="第 2 關打卡" description="用來測第 2 關打卡完成狀態。">
                <button
                  className="admin-btn admin-btn-action"
                  type="button"
                  disabled={isBusy || !account}
                  onClick={() => requestAction({
                    key: "complete-step2",
                    title: "確認完成第 2 關打卡？",
                    message: `系統會為 ${account || "此會員"} 寫入第 2 關打卡完成測試資料。`,
                    confirmText: "確認完成",
                    success: "第 2 關打卡已完成。",
                    run: () => api(`/api/admin/members/${encodeURIComponent(account)}/complete-step2-checkin`, {
                      method: "POST",
                      headers: adminHeaders(),
                      body: JSON.stringify({})
                    })
                  })}
                >
                  {actionLabel("complete-step2", "測試第 2 關打卡")}
                </button>
              </ActionLine>

              <ActionLine title="第 4 關問答" description="用來測第 4 關問答送出狀態。">
                <button
                  className="admin-btn admin-btn-action"
                  type="button"
                  disabled={isBusy || !account}
                  onClick={() => requestAction({
                    key: "step4-quiz",
                    title: "確認送出第 4 關問答？",
                    message: `系統會以 ${account || "此會員"} 送出第 4 關問答測試資料。`,
                    confirmText: "確認送出",
                    success: "第 4 關問答已送出。",
                    run: () => api("/api/public/newbie/step4-quiz", {
                      method: "POST",
                      body: JSON.stringify({ account, answers: ["slots", "quest", "split", "extra_bonus"] })
                    })
                  })}
                >
                  {actionLabel("step4-quiz", "測試第 4 關問答")}
                </button>
              </ActionLine>

              <ActionLine title="第 6 關新人啟航紅包" description="用來測第 6 關紅包開啟與派發流程。">
                <button
                  className="admin-btn admin-btn-action"
                  type="button"
                  disabled={isBusy || !account}
                  onClick={() => requestAction({
                    key: "step6-red-packet",
                    title: "確認建立第 6 關紅包測試資料？",
                    message: `系統會為 ${account || "此會員"} 檢查第 6 關新人啟航紅包，可能建立紅包派發測試資料。`,
                    confirmText: "確認建立",
                    success: "第 6 關新人啟航紅包已抽取或已存在。",
                    run: () => api(`/api/admin/members/${encodeURIComponent(account)}/check-step6-red-packet`, {
                      method: "POST",
                      headers: adminHeaders(),
                      body: JSON.stringify({ simulate_unlocked: true, prize_amount: 8888 })
                    })
                  })}
                >
                  {actionLabel("step6-red-packet", "測試第 6 關紅包")}
                </button>
              </ActionLine>

              <ActionLine title="第 7 關畢業派發" description="用來測第 7 關畢業獎勵建單與派發任務。">
                <button
                  className="admin-btn admin-btn-action"
                  type="button"
                  disabled={isBusy || !account}
                  onClick={() => requestAction({
                    key: "step7-graduation",
                    title: "確認建立第 7 關畢業派發？",
                    message: `系統會為 ${account || "此會員"} 建立或確認第 7 關畢業派發測試資料。`,
                    confirmText: "確認建立",
                    success: "第 7 關畢業派發已建立或已存在。",
                    run: () => api(`/api/admin/members/${encodeURIComponent(account)}/create-step7-graduation-rewards`, {
                      method: "POST",
                      headers: adminHeaders(),
                      body: JSON.stringify({ simulate_available: true })
                    })
                  })}
                >
                  {actionLabel("step7-graduation", "測試第 7 關畢業派發")}
                </button>
              </ActionLine>
            </div>
            <p className="admin-muted">第 7 關舊版直接畢業工具已隱藏；正式版仍以派發完成狀態判斷畢業。</p>
          </TestDataToolCard>

          <TestDataToolCard
            eyebrow="INVITE TEST"
            title="邀友支線測試資料"
            description="用來測邀友關係、好友首存檢查與邀友獎勵建單流程。"
          >
            <div className="test-data-form-grid compact">
              <label>邀請人<input value={memberAccount} onChange={(event) => setMemberAccount(event.target.value)} /></label>
              <label>好友帳號<input value={inviteeAccount} onChange={(event) => setInviteeAccount(event.target.value)} /></label>
            </div>
            <div className="test-data-action-list">
              <ActionLine title="建立邀友關係" description="建立邀請人與好友帳號之間的測試關係。">
                <button
                  className="admin-btn admin-btn-action"
                  type="button"
                  disabled={isBusy || !account || !invitee}
                  onClick={() => requestAction({
                    key: "invite-relation",
                    title: "確認建立邀友關係？",
                    message: `系統會建立 ${account || "邀請人"} 與 ${invitee || "好友"} 的邀友測試關係。`,
                    confirmText: "確認建立",
                    success: "邀友關係已建立。",
                    run: () => api(`/api/admin/members/${encodeURIComponent(account)}/invite-relations`, {
                      method: "POST",
                      headers: adminHeaders(),
                      body: JSON.stringify({ invitee_account: invitee })
                    })
                  })}
                >
                  {actionLabel("invite-relation", "建立邀友關係")}
                </button>
              </ActionLine>
              <ActionLine title="檢查邀友首存" description="用來測好友首存是否觸發邀友獎勵。">
                <button
                  className="admin-btn admin-btn-action"
                  type="button"
                  disabled={isBusy || !account || !invitee}
                  onClick={() => requestAction({
                    key: "invite-first-deposit",
                    title: "確認檢查邀友首存？",
                    message: `系統會檢查 ${invitee || "好友"} 的首存資料，並依既有規則處理邀友測試流程。`,
                    confirmText: "確認檢查",
                    success: "邀友首存已檢查。",
                    run: () => api(`/api/admin/members/${encodeURIComponent(account)}/check-invite-first-deposit`, {
                      method: "POST",
                      headers: adminHeaders(),
                      body: JSON.stringify({ invitee_account: invitee })
                    })
                  })}
                >
                  {actionLabel("invite-first-deposit", "測試邀友首存")}
                </button>
              </ActionLine>
            </div>
          </TestDataToolCard>
        </div>
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>PAYOUT FLOW TEST</span>
          <h2>派發鏈路測試</h2>
        </div>
        <p className="admin-muted">
          此區只整理既有 local/dev 測試入口，不新增測試資料功能。派發任務、派發結果、重試、取消與逾時仍以既有驗收流程為準。
        </p>
        <div className="test-data-tool-grid single">
          <TestDataToolCard
            eyebrow="GRADUATED MEMBER FLOW"
            title="畢業後 / 週末 / 常態回饋驗收工具"
            description="使用 test008 驗收畢業會員、週末耀旺、紅包券與常態回饋派發鏈路。"
          >
            <div className="test-data-action-list">
              <ActionLine title="建立畢業會員基礎資料" description="建立或確認 test008 的畢業會員基礎資料。">
                <button
                  className="admin-btn admin-btn-primary"
                  type="button"
                  disabled={isBusy}
                  onClick={() => requestAction({
                    key: "test008-graduated",
                    title: "確認建立畢業會員基礎資料？",
                    message: "系統會建立或確認 test008 的畢業會員基礎資料，供 local/dev 驗收使用。",
                    confirmText: "確認建立",
                    success: "test008 畢業會員基礎資料已建立或已存在。",
                    run: () => runTest008Action("prepare-graduated-member", "test008 畢業會員基礎資料已建立或已存在")
                  })}
                >
                  {actionLabel("test008-graduated", "建立畢業會員基礎資料")}
                </button>
              </ActionLine>

              <ActionLine title="建立週末耀旺測試存款" description="用來測週末任務與週末紅包券流程。">
                <button
                  className="admin-btn admin-btn-action"
                  type="button"
                  disabled={isBusy}
                  onClick={() => requestAction({
                    key: "test008-weekend",
                    title: "確認建立週末耀旺測試存款？",
                    message: "系統會為 test008 建立週末耀旺測試存款，可能讓同類測試資料變多。",
                    confirmText: "確認建立",
                    success: "test008 週末耀旺測試存款已建立或已存在。",
                    run: () => runTest008Action("create-weekend-deposits", "test008 週末耀旺測試存款已建立或已存在")
                  })}
                >
                  {actionLabel("test008-weekend", "建立週末耀旺測試存款")}
                </button>
              </ActionLine>

              <ActionLine title="建立常態回饋測試存款" description="用來測正式 deposits 觸發常態回饋與派發任務。">
                <button
                  className="admin-btn admin-btn-action"
                  type="button"
                  disabled={isBusy}
                  onClick={() => requestAction({
                    key: "test008-regular",
                    title: "確認建立常態回饋測試存款？",
                    message: "系統會為 test008 建立常態回饋測試存款，並沿用既有規則觸發測試流程。",
                    confirmText: "確認建立",
                    success: "test008 常態回饋測試存款已建立或已存在。",
                    run: () => runTest008Action("create-regular-rebate-deposit", "test008 常態回饋測試存款已建立或已存在")
                  })}
                >
                  {actionLabel("test008-regular", "建立常態回饋測試存款")}
                </button>
              </ActionLine>
            </div>
            <div className="test-data-nav-row">
              <button className="admin-btn admin-btn-secondary" type="button" onClick={() => { window.location.href = "/admin/members/test008"; }}>查看會員詳情</button>
              <button className="admin-btn admin-btn-secondary" type="button" onClick={() => goAsTest008(`/weekend-bonus?member_account=test008&test_now=${encodeURIComponent(weekendTestNow)}`)}>前往週末耀旺驗收</button>
              <button className="admin-btn admin-btn-secondary" type="button" onClick={() => goAsTest008("/red-packet")}>前往紅包券頁</button>
              <button className="admin-btn admin-btn-secondary" type="button" onClick={() => { window.location.href = "/admin/rewards"; }}>查看派發中心</button>
            </div>
          </TestDataToolCard>
        </div>
      </section>

      <section className="admin-card test-data-danger-zone">
        <div className="admin-card-head">
          <span>DANGER ZONE</span>
          <h2>危險操作</h2>
        </div>
        <p className="admin-muted">
          這裡只放清除、重建、批量產生等可能影響驗收結果的操作。執行前必須二次確認。
        </p>
        <div className="test-data-tool-grid single">
          <TestDataToolCard
            eyebrow="CLEAR LOCAL TEST DATA"
            title="清除 local 測試資料"
            description="清除後可能影響目前測試資料與驗收結果。請只在 local/dev 且確認沒有正在驗收時使用。"
            tone="danger"
          >
            <ActionLine title="清除測試資料" description="不可逆操作，按下確認後才會呼叫清除 API。">
              <button
                className="admin-btn admin-btn-danger"
                type="button"
                disabled={isBusy}
                onClick={() => requestAction({
                  key: "clear-test-data",
                  title: "確認清除測試資料？",
                  message: "此操作只應在 local/dev 使用。清除後可能影響目前測試資料與驗收結果。請確認你真的要執行。",
                  confirmText: "確認清除",
                  success: "local 測試資料已清除。",
                  variant: "danger",
                  run: () => api("/api/admin/clear-test-data", {
                    method: "POST",
                    headers: adminHeaders(),
                    body: JSON.stringify({})
                  })
                })}
              >
                {actionLabel("clear-test-data", "清除測試資料")}
              </button>
            </ActionLine>
          </TestDataToolCard>
        </div>
      </section>

      <ConfirmDialog
        open={Boolean(pendingAction)}
        title={pendingAction?.title ?? ""}
        message={pendingAction?.message ?? ""}
        confirmText={pendingAction?.confirmText ?? "確認執行"}
        cancelText="取消"
        variant={pendingAction?.variant ?? "normal"}
        loading={Boolean(activeActionKey)}
        onCancel={() => {
          if (!activeActionKey) setPendingAction(null);
        }}
        onConfirm={confirmPendingAction}
      />
    </AdminLayout>
  );
}
